import React, { useState, useEffect } from 'react';
import {
  Card,
  Typography,
  Row,
  Col,
  Avatar,
  Tag,
  Descriptions,
  Spin,
  message,
  Button,
  Space,
  Divider,
  Statistic,
  List,
  Badge,
  Empty,
  Table,
  Modal,
  Pagination
} from 'antd';
import {
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  CalendarOutlined,
  EnvironmentOutlined,
  ShoppingCartOutlined,
  DollarOutlined,
  TrophyOutlined,
  ClockCircleOutlined,
  ArrowLeftOutlined,
  EyeOutlined,
  FileTextOutlined,
  SettingOutlined
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { userService } from '../../services';
import { formatDate, formatCurrency } from '../../utils/helpers';

const { Title, Text } = Typography;
const { Item } = Descriptions;

const UserDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // State for user data
  const [userDetail, setUserDetail] = useState(null);
  const [userAddresses, setUserAddresses] = useState([]);
  const [userOrderStats, setUserOrderStats] = useState(null);
  const [userOrders, setUserOrders] = useState([]);
  const [ordersPagination, setOrdersPagination] = useState({ offset: 0, limit: 10, count: 0 });
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetailModal, setOrderDetailModal] = useState(false);
  const [statusChangeModal, setStatusChangeModal] = useState(false);
  const [statusChangeLoading, setStatusChangeLoading] = useState(false);
  const [loading, setLoading] = useState({
    detail: false,
    addresses: false,
    stats: false,
    orders: false,
    orderDetail: false
  });

  // Load user data on component mount
  useEffect(() => {
    if (id) {
      loadUserData();
    }
  }, [id]);

  const loadUserData = async () => {
    try {
      // Load user detail
      setLoading(prev => ({ ...prev, detail: true }));
      const detailResponse = await userService.getDetail(id);
      if (detailResponse.success) {
        setUserDetail(detailResponse.data.user);
      }

      // Load user addresses
      setLoading(prev => ({ ...prev, addresses: true }));
      const addressesResponse = await userService.getAddresses(id);
      if (addressesResponse.success) {
        setUserAddresses(addressesResponse.data.addresses || []);
      }

      // Load user order stats
      setLoading(prev => ({ ...prev, stats: true }));
      const statsResponse = await userService.getOrderStats(id);
      if (statsResponse.success) {
        setUserOrderStats(statsResponse.data);
      }

      // Load user orders
      await loadUserOrders();

    } catch (error) {
      //console.error('Error loading user data:', error);
      if (error.response?.status === 401) {
        message.error('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.');
        navigate('/login');
      } else if (error.response?.status === 403) {
        message.error('Bạn không có quyền xem thông tin người dùng này');
        navigate('/users/list');
      } else if (error.response?.status === 404) {
        message.error('Người dùng không tồn tại');
        navigate('/users/list');
      } else {
        message.error('Có lỗi xảy ra khi tải thông tin người dùng');
      }
    } finally {
      setLoading({ detail: false, addresses: false, stats: false, orders: false });
    }
  };

  const loadUserOrders = async (offset = 0, limit = 10) => {
    try {
      setLoading(prev => ({ ...prev, orders: true }));
      const ordersResponse = await userService.getOrders(id, { offset, limit });
      if (ordersResponse.success) {
        setUserOrders(ordersResponse.data.orders || []);
        setOrdersPagination({
          offset: ordersResponse.data.pagination.offset,
          limit: ordersResponse.data.pagination.limit,
          count: ordersResponse.data.count
        });
      }
    } catch (error) {
      //console.error('Error loading user orders:', error);
      message.error('Có lỗi xảy ra khi tải danh sách đơn hàng');
    } finally {
      setLoading(prev => ({ ...prev, orders: false }));
    }
  };

  const loadOrderDetail = async (invoiceId) => {
    try {
      setLoading(prev => ({ ...prev, orderDetail: true }));
      const detailResponse = await userService.getOrderDetail(invoiceId);
      if (detailResponse.success) {
        setSelectedOrder(detailResponse.data);
        setOrderDetailModal(true);
      }
    } catch (error) {
      //console.error('Error loading order detail:', error);
      if (error.response?.status === 404) {
        message.error('Đơn hàng không tồn tại');
      } else {
        message.error('Có lỗi xảy ra khi tải chi tiết đơn hàng');
      }
    } finally {
      setLoading(prev => ({ ...prev, orderDetail: false }));
    }
  };

  // Get role display name and color
  const getRoleInfo = (role) => {
    const roleMap = {
      'admin': { name: 'Quản trị viên', color: 'red' },
      'manager': { name: 'Quản lý', color: 'orange' },
      'stock': { name: 'Nhân viên kho', color: 'blue' },
      'member': { name: 'Thành viên', color: 'green' },
      'customer': { name: 'Khách hàng', color: 'purple' }
    };
    return roleMap[role] || { name: role, color: 'default' };
  };

  // Get status display name and color
  const getStatusInfo = (status) => {
    const statusMap = {
      'active': { name: 'Hoạt động', color: 'green', icon: 'success' },
      'inactive': { name: 'Không hoạt động', color: 'orange', icon: 'warning' },
      'banned': { name: 'Bị cấm', color: 'red', icon: 'error' }
    };
    return statusMap[status] || { name: status, color: 'default', icon: 'default' };
  };

  // Get customer level color
  const getCustomerLevelColor = (level) => {
    const levelColors = {
      'VIP': '#722ed1',
      'Gold': '#faad14',
      'Silver': '#d9d9d9',
      'Bronze': '#a6a6a6'
    };
    return levelColors[level] || '#d9d9d9';
  };

  // Get order status display name and color
  const getOrderStatusInfo = (statusId) => {
    const statusMap = {
      1: { name: 'Chờ xử lý', color: 'orange', icon: 'clock-circle' },
      2: { name: 'Đang xử lý', color: 'blue', icon: 'loading' },
      3: { name: 'Đã giao hàng', color: 'green', icon: 'check-circle' },
      4: { name: 'Đã hủy', color: 'red', icon: 'close-circle' },
      5: { name: 'Hoàn trả', color: 'purple', icon: 'rollback' }
    };
    return statusMap[statusId] || { name: 'Không xác định', color: 'default', icon: 'question-circle' };
  };

  // Handle status change
  const handleStatusChange = () => {
    setStatusChangeModal(true);
  };

  // Handle status change submit
  const handleStatusChangeSubmit = async (newStatus) => {
    if (!userDetail || !newStatus) return;

    try {
      setStatusChangeLoading(true);
      const response = await userService.updateStatus(userDetail.id, newStatus);

      if (response.success) {
        const statusInfo = getStatusInfo(newStatus);
        message.success(`Đã thay đổi trạng thái của ${userDetail.full_name} thành ${statusInfo.name}`);
        setStatusChangeModal(false);
        // Update local state
        setUserDetail(prev => ({ ...prev, status: newStatus }));
      } else {
        message.error(response.message || 'Có lỗi xảy ra khi thay đổi trạng thái');
      }
    } catch (error) {
      //console.error('Error updating user status:', error);
      if (error.response?.status === 403) {
        message.error('Bạn không có quyền thay đổi trạng thái của người dùng này');
      } else if (error.response?.status === 401) {
        message.error('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.');
        navigate('/login');
      } else {
        message.error('Có lỗi xảy ra khi thay đổi trạng thái người dùng');
      }
    } finally {
      setStatusChangeLoading(false);
    }
  };

  // Handle status change cancel
  const handleStatusChangeCancel = () => {
    setStatusChangeModal(false);
  };

  if (!userDetail && !loading.detail) {
    return (
      <div style={{ padding: 24 }}>
        <Card>
          <Empty
            description="Không tìm thấy thông tin người dùng"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            <Button type="primary" onClick={() => navigate('/users/list')}>
              Quay lại danh sách
            </Button>
          </Empty>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/users/list')}
          style={{ marginBottom: 16 }}
        >
          Quay lại danh sách
        </Button>

        <Row gutter={16} align="middle">
          <Col>
            <Avatar
              size={64}
              icon={<UserOutlined />}
              style={{ backgroundColor: '#1890ff' }}
            >
              {userDetail?.full_name?.charAt(0)?.toUpperCase()}
            </Avatar>
          </Col>
          <Col>
            <Title level={2} style={{ margin: 0 }}>
              {userDetail?.full_name || 'Đang tải...'}
            </Title>
            <Space>
              <Text type="secondary">@{userDetail?.username}</Text>
              {userDetail && (
                <>
                  <Tag color={getRoleInfo(userDetail.role).color}>
                    {getRoleInfo(userDetail.role).name}
                  </Tag>
                  <Badge
                    status={getStatusInfo(userDetail.status).icon}
                    text={getStatusInfo(userDetail.status).name}
                  />
                </>
              )}
            </Space>
          </Col>
          <Col flex="auto" style={{ textAlign: 'right' }}>
            <Space>
              <Button
                icon={<SettingOutlined />}
                onClick={handleStatusChange}
                style={{ backgroundColor: '#fa8c16', borderColor: '#fa8c16', color: 'white' }}
              >
                Thay đổi trạng thái
              </Button>
            </Space>
          </Col>
        </Row>
      </div>

      <Row gutter={24}>
        {/* User Information */}
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <UserOutlined />
                Thông tin cá nhân
              </Space>
            }
            loading={loading.detail}
          >
            {userDetail && (
              <Descriptions column={1} bordered>
                <Item label="ID người dùng">
                  <Text strong style={{ fontFamily: 'monospace' }}>
                    #{userDetail.id}
                  </Text>
                </Item>
                <Item label="Họ và tên">{userDetail.full_name}</Item>
                <Item label="Tên đăng nhập">{userDetail.username}</Item>
                <Item label="Email">
                  <Space>
                    <MailOutlined style={{ color: '#1890ff' }} />
                    {userDetail.email}
                  </Space>
                </Item>
                <Item label="Số điện thoại">
                  <Space>
                    <PhoneOutlined style={{ color: '#52c41a' }} />
                    {userDetail.SDT || 'Chưa cập nhật'}
                  </Space>
                </Item>
                <Item label="Ngày sinh">
                  <Space>
                    <CalendarOutlined style={{ color: '#fa8c16' }} />
                    {userDetail.NgaySinh ? formatDate(userDetail.NgaySinh, 'DD/MM/YYYY') : 'Chưa cập nhật'}
                  </Space>
                </Item>
                <Item label="Giới tính">{userDetail.GioiTinh || 'Chưa cập nhật'}</Item>
                <Item label="Vai trò">
                  <Tag color={getRoleInfo(userDetail.role).color}>
                    {getRoleInfo(userDetail.role).name}
                  </Tag>
                </Item>
                <Item label="Trạng thái">
                  <Badge
                    status={getStatusInfo(userDetail.status).icon}
                    text={getStatusInfo(userDetail.status).name}
                  />
                </Item>
                <Item label="Ngày tạo">{formatDate(userDetail.created_at)}</Item>
                <Item label="Cập nhật cuối">{formatDate(userDetail.updated_at)}</Item>
              </Descriptions>
            )}
          </Card>
        </Col>

        {/* Order Statistics */}
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <ShoppingCartOutlined />
                Thống kê đơn hàng
              </Space>
            }
            loading={loading.stats}
          >
            {userOrderStats?.stats ? (
              <Row gutter={16}>
                <Col span={12}>
                  <Statistic
                    title="Tổng đơn hàng"
                    value={userOrderStats.stats.total_orders}
                    prefix={<ShoppingCartOutlined />}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="Tổng chi tiêu"
                    value={userOrderStats.stats.total_spent}
                    prefix={<DollarOutlined />}
                    formatter={(value) => formatCurrency(value)}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="Đơn hàng cuối"
                    value={userOrderStats.stats.last_order_date ?
                      formatDate(userOrderStats.stats.last_order_date, 'DD/MM/YYYY') :
                      'Chưa có'
                    }
                    prefix={<ClockCircleOutlined />}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="Giá trị TB/đơn"
                    value={userOrderStats.stats.avg_order_value || 0}
                    formatter={(value) => formatCurrency(value)}
                    valueStyle={{ color: '#fa8c16' }}
                  />
                </Col>

                {userOrderStats.summary && (
                  <>
                    <Divider />
                    <Col span={12}>
                      <Space direction="vertical" size="small">
                        <Text strong>Cấp độ khách hàng</Text>
                        <Tag
                          color={getCustomerLevelColor(userOrderStats.summary.customerLevel)}
                          icon={<TrophyOutlined />}
                        >
                          {userOrderStats.summary.customerLevel}
                        </Tag>
                      </Space>
                    </Col>
                    <Col span={12}>
                      <Space direction="vertical" size="small">
                        <Text strong>Tần suất mua hàng</Text>
                        <Tag color="blue">
                          {userOrderStats.summary.orderFrequency}
                        </Tag>
                      </Space>
                    </Col>
                  </>
                )}
              </Row>
            ) : (
              <Empty
                description="Người dùng chưa có đơn hàng nào"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )}
          </Card>
        </Col>
      </Row>

      {/* Addresses */}
      <Card
        title={
          <Space>
            <EnvironmentOutlined />
            Danh sách địa chỉ ({userAddresses.length})
          </Space>
        }
        style={{ marginTop: 24 }}
        loading={loading.addresses}
      >
        {userAddresses.length > 0 ? (
          <List
            dataSource={userAddresses}
            renderItem={(address) => (
              <List.Item>
                <List.Item.Meta
                  avatar={
                    <Avatar
                      icon={<EnvironmentOutlined />}
                      style={{
                        backgroundColor: address.is_default ? '#52c41a' : '#d9d9d9'
                      }}
                    />
                  }
                  title={
                    <Space>
                      <Text strong>{address.address_name}</Text>
                      {address.is_default && <Tag color="green">Mặc định</Tag>}
                      <Tag color="blue">{address.type === 'home' ? 'Nhà riêng' : 'Công ty'}</Tag>
                    </Space>
                  }
                  description={
                    <div>
                      <div>{address.street}</div>
                      <div style={{ color: '#666' }}>
                        {address.ward}, {address.district}, {address.province}
                      </div>
                      <div style={{ fontSize: '12px', color: '#999', marginTop: 4 }}>
                        Cập nhật: {formatDate(address.updated_at)}
                      </div>
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        ) : (
          <Empty
            description="Người dùng chưa có địa chỉ nào"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        )}
      </Card>

      {/* User Orders */}
      <Card
        title={
          <Space>
            <ShoppingCartOutlined />
            Lịch sử đơn hàng ({ordersPagination.count})
          </Space>
        }
        style={{ marginTop: 24 }}
        loading={loading.orders}
      >
        {userOrders.length > 0 ? (
          <>
            <Table
              dataSource={userOrders}
              rowKey="id"
              pagination={false}
              size="small"
              columns={[
                {
                  title: 'Mã đơn hàng',
                  dataIndex: 'code',
                  key: 'code',
                  render: (code) => (
                    <Text strong style={{ fontFamily: 'monospace' }}>
                      {code}
                    </Text>
                  )
                },
                {
                  title: 'Trạng thái',
                  dataIndex: 'status_id',
                  key: 'status_id',
                  render: (statusId) => {
                    const statusInfo = getOrderStatusInfo(statusId);
                    return (
                      <Tag color={statusInfo.color}>
                        {statusInfo.name}
                      </Tag>
                    );
                  }
                },
                {
                  title: 'Tổng tiền',
                  dataIndex: 'total_amount',
                  key: 'total_amount',
                  render: (amount) => (
                    <Text strong style={{ color: '#52c41a' }}>
                      {formatCurrency(amount)}
                    </Text>
                  ),
                  align: 'right'
                },
                {
                  title: 'Ngày tạo',
                  dataIndex: 'created_at',
                  key: 'created_at',
                  render: (date) => formatDate(date, 'DD/MM/YYYY HH:mm')
                },
                {
                  title: 'Ghi chú',
                  dataIndex: 'note',
                  key: 'note',
                  render: (note) => note || '-',
                  ellipsis: true
                },
                {
                  title: 'Thao tác',
                  key: 'action',
                  render: (_, record) => (
                    <Button
                      type="link"
                      icon={<EyeOutlined />}
                      onClick={() => loadOrderDetail(record.id)}
                      size="small"
                    >
                      Chi tiết
                    </Button>
                  ),
                  align: 'center'
                }
              ]}
            />

            {ordersPagination.count > ordersPagination.limit && (
              <div style={{ textAlign: 'center', marginTop: 16 }}>
                <Pagination
                  current={Math.floor(ordersPagination.offset / ordersPagination.limit) + 1}
                  total={ordersPagination.count}
                  pageSize={ordersPagination.limit}
                  onChange={(page) => {
                    const newOffset = (page - 1) * ordersPagination.limit;
                    loadUserOrders(newOffset, ordersPagination.limit);
                  }}
                  showSizeChanger={false}
                  showQuickJumper
                  showTotal={(total, range) =>
                    `${range[0]}-${range[1]} của ${total} đơn hàng`
                  }
                />
              </div>
            )}
          </>
        ) : (
          <Empty
            description="Người dùng chưa có đơn hàng nào"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        )}
      </Card>

      {/* Order Detail Modal */}
      <Modal
        title={
          <Space>
            <FileTextOutlined />
            Chi tiết đơn hàng {selectedOrder?.invoice?.code}
          </Space>
        }
        open={orderDetailModal}
        onCancel={() => setOrderDetailModal(false)}
        width={1000}
        footer={[
          <Button key="close" onClick={() => setOrderDetailModal(false)}>
            Đóng
          </Button>
        ]}
        loading={loading.orderDetail}
      >
        {selectedOrder && (
          <div>
            {/* Order Information */}
            <Card size="small" style={{ marginBottom: 16 }}>
              <Descriptions
                title="Thông tin đơn hàng"
                column={2}
                size="small"
                bordered
              >
                <Descriptions.Item label="Mã đơn hàng">
                  <Text strong style={{ fontFamily: 'monospace' }}>
                    {selectedOrder.invoice.code}
                  </Text>
                </Descriptions.Item>
                <Descriptions.Item label="Trạng thái">
                  <Tag color={getOrderStatusInfo(selectedOrder.invoice.status_id).color}>
                    {getOrderStatusInfo(selectedOrder.invoice.status_id).name}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Khách hàng">
                  {selectedOrder.invoice.customer_name}
                </Descriptions.Item>
                <Descriptions.Item label="Ngày tạo">
                  {formatDate(selectedOrder.invoice.created_at, 'DD/MM/YYYY HH:mm')}
                </Descriptions.Item>
                <Descriptions.Item label="Tổng tiền" span={2}>
                  <Text strong style={{ color: '#52c41a', fontSize: '16px' }}>
                    {formatCurrency(selectedOrder.invoice.total_amount)}
                  </Text>
                </Descriptions.Item>
                {selectedOrder.invoice.note && (
                  <Descriptions.Item label="Ghi chú" span={2}>
                    {selectedOrder.invoice.note}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>

            {/* Shipping Address */}
            {(selectedOrder.invoice.common_address || selectedOrder.invoice.shipping_address) && (
              <Card size="small" style={{ marginBottom: 16 }}>
                <Descriptions
                  title="Địa chỉ giao hàng"
                  column={1}
                  size="small"
                  bordered
                >
                  {selectedOrder.invoice.shipping_address ? (
                    <>
                      <Descriptions.Item label="Người nhận">
                        {selectedOrder.invoice.shipping_address.receiver_name}
                      </Descriptions.Item>
                      <Descriptions.Item label="Số điện thoại">
                        {selectedOrder.invoice.shipping_address.phone}
                      </Descriptions.Item>
                      <Descriptions.Item label="Địa chỉ">
                        {selectedOrder.invoice.shipping_address.house_number} {selectedOrder.invoice.shipping_address.street},<br />
                        {selectedOrder.invoice.shipping_address.ward}, {selectedOrder.invoice.shipping_address.district},<br />
                        {selectedOrder.invoice.shipping_address.province}
                      </Descriptions.Item>
                      {selectedOrder.invoice.shipping_address.address_detail && (
                        <Descriptions.Item label="Chi tiết">
                          {selectedOrder.invoice.shipping_address.address_detail}
                        </Descriptions.Item>
                      )}
                    </>
                  ) : selectedOrder.invoice.common_address ? (
                    <>
                      <Descriptions.Item label="Người nhận">
                        {selectedOrder.invoice.common_address.receiver_name}
                      </Descriptions.Item>
                      <Descriptions.Item label="Số điện thoại">
                        {selectedOrder.invoice.common_address.phone}
                      </Descriptions.Item>
                      <Descriptions.Item label="Địa chỉ">
                        {selectedOrder.invoice.common_address.house_number} {selectedOrder.invoice.common_address.street},<br />
                        {selectedOrder.invoice.common_address.ward}, {selectedOrder.invoice.common_address.district},<br />
                        {selectedOrder.invoice.common_address.province}
                      </Descriptions.Item>
                      {selectedOrder.invoice.common_address.address_detail && (
                        <Descriptions.Item label="Chi tiết">
                          {selectedOrder.invoice.common_address.address_detail}
                        </Descriptions.Item>
                      )}
                    </>
                  ) : null}
                </Descriptions>
              </Card>
            )}

            {/* Order Items */}
            <Card size="small">
              <Descriptions
                title={`Chi tiết sản phẩm (${selectedOrder.summary.total_items} sản phẩm, ${selectedOrder.summary.total_quantity} cái)`}
                column={1}
                size="small"
              />
              <Table
                dataSource={selectedOrder.items}
                rowKey="variant_id"
                pagination={false}
                size="small"
                columns={[
                  {
                    title: 'Sản phẩm',
                    dataIndex: 'product_name',
                    key: 'product_name',
                    render: (name, record) => (
                      <div>
                        <div style={{ fontWeight: 'bold' }}>{name}</div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          SKU: {record.variant_sku}
                        </div>
                        {record.variant_attributes && (
                          <div style={{ fontSize: '12px', color: '#999' }}>
                            {record.variant_attributes}
                          </div>
                        )}
                      </div>
                    )
                  },
                  {
                    title: 'Số lượng',
                    dataIndex: 'quantity',
                    key: 'quantity',
                    align: 'center',
                    width: 80
                  },
                  {
                    title: 'Đơn giá',
                    dataIndex: 'price',
                    key: 'price',
                    render: (price) => formatCurrency(price),
                    align: 'right',
                    width: 120
                  },
                  {
                    title: 'Thành tiền',
                    dataIndex: 'amount',
                    key: 'amount',
                    render: (amount) => (
                      <Text strong style={{ color: '#1890ff' }}>
                        {formatCurrency(amount)}
                      </Text>
                    ),
                    align: 'right',
                    width: 120
                  }
                ]}
              />

              {/* Order Summary */}
              <Divider />
              <Row justify="end" style={{ marginTop: 16 }}>
                <Col span={12}>
                  <Descriptions
                    column={1}
                    size="small"
                    bordered={false}
                    labelStyle={{ fontWeight: 'bold' }}
                  >
                    <Descriptions.Item label="Tổng tiền hàng">
                      {formatCurrency(selectedOrder.invoice.sub_total || selectedOrder.summary.items_total_amount)}
                    </Descriptions.Item>
                    {selectedOrder.invoice.discount_amount > 0 && (
                      <Descriptions.Item label="Giảm giá">
                        -{formatCurrency(selectedOrder.invoice.discount_amount)}
                      </Descriptions.Item>
                    )}
                    {selectedOrder.invoice.shipping_fee > 0 && (
                      <Descriptions.Item label="Phí vận chuyển">
                        {formatCurrency(selectedOrder.invoice.shipping_fee)}
                      </Descriptions.Item>
                    )}
                    <Descriptions.Item label="Tổng cộng">
                      <Text strong style={{ color: '#52c41a', fontSize: '16px' }}>
                        {formatCurrency(selectedOrder.invoice.total_amount)}
                      </Text>
                    </Descriptions.Item>
                  </Descriptions>
                </Col>
              </Row>
            </Card>
          </div>
        )}
      </Modal>

      {/* Status Change Modal */}
      <Modal
        title="Thay đổi trạng thái người dùng"
        open={statusChangeModal}
        onCancel={handleStatusChangeCancel}
        footer={null}
        width={500}
      >
        {userDetail && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <strong>Người dùng:</strong> {userDetail.full_name}
            </div>
            <div style={{ marginBottom: 16 }}>
              <strong>Trạng thái hiện tại:</strong>{' '}
              <Badge
                status={getStatusInfo(userDetail.status).icon}
                text={getStatusInfo(userDetail.status).name}
              />
            </div>
            <div style={{ marginBottom: 24 }}>
              <strong>Chọn trạng thái mới:</strong>
            </div>
            <Row gutter={[16, 16]}>
              {['active', 'inactive', 'banned'].map(status => {
                const statusInfo = getStatusInfo(status);
                return (
                  <Col span={12} key={status}>
                    <Button
                      type={userDetail.status === status ? 'primary' : 'default'}
                      block
                      loading={statusChangeLoading}
                      onClick={() => handleStatusChangeSubmit(status)}
                      disabled={userDetail.status === status}
                      style={{
                        height: 'auto',
                        padding: '12px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      <div style={{ fontWeight: 'bold' }}>
                        {statusInfo.name}
                      </div>
                      <Badge
                        status={statusInfo.icon}
                        text={
                          <Space>
                            {statusInfo.icon}
                            {status.toUpperCase()}
                          </Space>
                        }
                      />
                    </Button>
                  </Col>
                );
              })}
            </Row>
            <div style={{ marginTop: 24, textAlign: 'center' }}>
              <Button onClick={handleStatusChangeCancel}>
                Hủy
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default UserDetail;
